import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
	BRAND_NAME,
	COOKIES,
	IS_ADMIN,
	MARKETING_URL,
	UTM_TAGS_TTL_DAYS,
} from '@/services/Constants';
import { isSecure, setCookie, setUtmTags } from '@/services/Helper';
import useSearchParams from '@/services/hocs/useSearchParams';
import { setAccessToken } from '@/services/AccessToken';
import { historyPush } from '@/services/History';
import { loginRequest } from '../store/actions';

import Button from '@/components/button/Button';
import HelmetWrapper from '@/components/HelmetWrapper';
import IntroWrapper from '@/components/introWrapper/IntroWrapper';
import Spinner from '@/components/spinner/Spinner';
import SignInForm from './forms/SignInForm';

import type { RootState } from '@/rootState';

type LocationState = { from?: { pathname: string } } | null;

const SignIn = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const location = useLocation();
	const router = useSearchParams();
	const authToken = router.get('code');

	const loading = useSelector((state: RootState) => state.signInReducer.loading);
	const socialLoading = useSelector((state: RootState) => state.signInReducer.socialLoading);

	const locationState = location.state as LocationState;
	const path = locationState?.from?.pathname
		? `${locationState.from.pathname}${location.search}`
		: '/';

	useEffect(() => {
		const querySearch = location.search;
		if (querySearch) setUtmTags(querySearch);

		if (path.split('/')[1] === 'coupon' && path.split('/')[2]) {
			setCookie(COOKIES.coupon, path.split('/')[2], {
				expires: UTM_TAGS_TTL_DAYS,
				secure: isSecure(),
			});
		}
	}, [path]);

	useEffect(() => {
		if (authToken) {
			setAccessToken(authToken);
			historyPush('/');
		}
	}, [authToken]);

	const handleSubmitForm = (email_or_username: string, password: string) => {
		dispatch(loginRequest(email_or_username, password, path));
	};

	return (
		<>
			<HelmetWrapper title="titles.login" description="meta_descriptions.login" />
			<IntroWrapper className="intro-wrapper--auth">
				<div className="intro-form">
					<h1 className="intro-title">
						{IS_ADMIN
							? t('sign_in.admin', { brandName: BRAND_NAME })
							: t('sign_in.login_to', { brandName: BRAND_NAME })}
					</h1>

					{socialLoading ? (
						<Spinner classname="medium" />
					) : (
						<SignInForm loading={loading} onFormSubmit={handleSubmitForm} />
					)}
				</div>
				<div className="btn-group additional-btn-group center">
					{!IS_ADMIN && (
						<Button
							href={`${MARKETING_URL}bestellen/prijzen`}
							type="link-border"
							title={t('sign_in.do_not_have_account')}
						/>
					)}
					<Button
						link="/wachtwoord-vergeten"
						type="link-border"
						title={t('sign_in.forgot_password')}
					/>
				</div>
			</IntroWrapper>
		</>
	);
};

export default memo(SignIn);
